// Copyright (c) Docugami, Inc. All rights reserved.

import { ISlotItem, ChunkSlotItem, SlotItemType } from "components/cards/ItemSlot";
import { BindingProviderId } from "networking/reportsApi";
import { useMemo } from "react";
import { DgmlInstancesSingleton, useDocumentDgml } from "./documentDgml";
import { TokenRange } from "networking/api";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { SpanIdUtils } from "utils/SpanIdUtils";
import { chunkSlotItemReadingOrderComparerThunk } from "./reportEntrySlots";
import { ChunkStructure } from "store/dgml/dgmlTypes";
import { store } from "store";
import { getParentTable, getSimpleColumns, isSimpleTable } from "../store/dgml/dgmlActions";
import { useAppDispatch, useAppSelector } from "./redux";
import { selectEntries, selectReportEntryBindings } from "store/reports/selectors";
import { folderIdSelector } from "store/dgml/dgmlSelectors";

enum BindingStructureType {
    Text = "Text",
    Table = "Table",
    TableRow = "TableRow",
    TableCell = "TableCell",
    List = "List",
    ListItem = "ListItem"
}

export interface BindingSuggestion {
    spanId: string;
    provider: BindingProviderId;
}

export const useRedLoopSuggestions = (entryId: string, folderId: string): BindingSuggestion[] => {
    const { fileId } = folderIdSelector(folderId)!;
    const entryBinding = useAppSelector(state => selectReportEntryBindings(state, fileId)?.entities[entryId]);

    return useMemo((): BindingSuggestion[] => {
        return entryBinding?.suggestions?.filter(x => !!x.spanId).map(x => ({
            spanId: x.spanId!,
            provider: x.bindingProviderId
        })) ?? [];
    }, [entryBinding]);
}

export const useSemanticSuggestions = (entryId: string, folderId: string): BindingSuggestion[] => {
    const { fileId } = folderIdSelector(folderId)!;
    const dgml = useDocumentDgml();
    const entry = useAppSelector(state => selectEntries(state)?.entities[entryId]);
    const entryBinding = useAppSelector(state => selectReportEntryBindings(state, fileId)?.entities[entryId]);

    return useMemo(() => {
        if (!dgml.isInitialized() || !entry?.title) {
            return [];
        }

        const bindingIds = entryBinding?.bindings.map(x => x.spanId ?? '').filter(x => !!x) ?? [];
        const structureFilter = getStructureFilterForSpanIds(folderId, bindingIds);

        let suggestionSpanIds: string[] = [];
        if (structureFilter === BindingStructureType.TableCell) {
            suggestionSpanIds = getColumnOrRowSuggestions(folderId, bindingIds);
        }

        if (!suggestionSpanIds.length) {
            suggestionSpanIds = dgml.getSpanIdsForLabel(entry.title);
        }

        if (suggestionSpanIds.length > 0) {
            if (structureFilter !== BindingStructureType.Text) {
                // Homogenous structure for all bindings - filter suggestions to the same type
                suggestionSpanIds = filterSpanIdsByStructureType(folderId, suggestionSpanIds, structureFilter);
            }

            suggestionSpanIds = filterSpanIdsByTokenRange(folderId, suggestionSpanIds, bindingIds);
        }

        return suggestionSpanIds.map<BindingSuggestion>(x => ({
            spanId: x,
            provider: BindingProviderId.System
        }));
    }, [entry?.title, dgml, folderId, entryBinding?.bindings])
}

export const useSlotsForSuggestions = (suggestions: BindingSuggestion[]): ISlotItem[] => {
    const dgml = useDocumentDgml();
    const dispatch = useAppDispatch();

    return useMemo(() => {
        if (!dgml.isInitialized()) {
            return [];
        }

        const slots: ChunkSlotItem[] = [];
        for (const suggestion of suggestions) {
            const instance = dgml.getInstanceById(suggestion.spanId);
            if (!!instance) {
                const slot: ChunkSlotItem = {
                    type: SlotItemType.Chunk,
                    id: suggestion.spanId,
                    spanId: instance.spanId,
                }

                slots.push(slot);
            }
        }

        return dispatch(chunkSlotItemReadingOrderComparerThunk(slots))
    }, [dgml, suggestions, dispatch])
}

const chunkStructureToBindingStructure = (chunkStructure?: ChunkStructure): BindingStructureType => {
    switch (chunkStructure) {
        case ChunkStructure.Table:
            return BindingStructureType.Table;
        case ChunkStructure.TableRow:
            return BindingStructureType.TableRow;
        case ChunkStructure.TableCell:
            return BindingStructureType.TableCell;
        case ChunkStructure.OrderedList:
        case ChunkStructure.UnorderedList:
            return BindingStructureType.List;
        default:
            return BindingStructureType.Text
    }
}

const getStructureFilterForSpanIds = (folderId: string, spanIds: string[]): BindingStructureType => {
    let targetStructureType: BindingStructureType | undefined;
    for (const spanId of spanIds) {
        const chunk = DgmlInstancesSingleton.getInstanceById(folderId, spanId);
        if (!!chunk?.chunk) {
            const structure = chunk.chunk.structure?.type;
            const structureType = chunkStructureToBindingStructure(structure);

            if (!targetStructureType) {
                targetStructureType = structureType;
            }
            else if (targetStructureType !== structureType) {
                // Non-homogenous structure so we can simply fallback to default
                targetStructureType = BindingStructureType.Text;
                break;
            }
        }
    }

    return targetStructureType ?? BindingStructureType.Text;
}

const filterSpanIdsByStructureType = (folderId: string, spanIds: string[], structureType: BindingStructureType): string[] =>
    spanIds.filter(x => chunkStructureToBindingStructure(DgmlInstancesSingleton.getInstanceById(folderId, x)?.chunk?.structure?.type) === structureType);

const getColumnOrRowSuggestions = (folderId: string, spanIds: string[]): string[] => {
    if (!spanIds.length) {
        return [];
    }

    const rowCounts: Record<string, SpanIdsWithHitCount> = {};
    const colCounts: Record<string, SpanIdsWithHitCount> = {};

    for (const spanId of spanIds) {
        const chunk = DgmlInstancesSingleton.getInstanceById(folderId, spanId)?.chunk;

        const rowChunk = DgmlInstancesSingleton.getInstanceById(folderId, chunk?.parent ?? '')?.chunk;
        const parentTable = store.dispatch(getParentTable(folderId, spanId));
        if (rowChunk?.structure?.type === ChunkStructure.TableRow) {
            const rowHit = rowCounts[rowChunk.spanId] ?? { spanIds: rowChunk.children, count: 0 };
            rowHit.count++;
            rowCounts[rowChunk.spanId] = rowHit;

            if (store.dispatch(isSimpleTable(folderId, parentTable?.spanId ?? ''))) {
                const columns = store.dispatch(getSimpleColumns(folderId, parentTable!.spanId));
                const colIndex = rowChunk.children.indexOf(chunk!.spanId);
                const column = columns[colIndex];
                if (!!column && column?.rows.findIndex(x => x.spanId === chunk!.spanId) !== -1) {
                    const colHit = colCounts[column.header.spanId] ?? { spanIds: column.rows.map(x => x.spanId), count: 0 };
                    colHit.count++;
                    colCounts[column.header.spanId] = colHit;
                }
            }
        }
    }

    const colHits = Object.values(colCounts);
    const results = colHits.filter(x => x.count > 1).flatMap(x => x.spanIds);
    if (!results.length) {
        const rowHits = Object.values(rowCounts);
        results.push(...rowHits.filter(x => x.count > 1).flatMap(x => x.spanIds));
    }

    return results;
}

type SpanIdsWithHitCount = {
    spanIds: string[];
    count: number;
}

type TokenAndSpan = {
    tokenRange: TokenRange;
    spanId: string;
}

const filterSpanIdsByTokenRange = (folderId: string, spanIds: string[], exclusionFilterSpanIds: string[] = []): string[] => {
    const isParent = (parent: TokenAndSpan, child: TokenAndSpan) => {
        if (TokenRangeUtils.rangeIsContained(child.tokenRange, parent.tokenRange)) {
            const parentSpanIdParts = SpanIdUtils.parse(parent.spanId);
            return !TokenRangeUtils.rangesEqual(parent.tokenRange, child.tokenRange) || !!parentSpanIdParts.subtreeId;
        }
    }

    const exclusionFilterItems: TokenAndSpan[] = [];
    for (const filterSpanId of exclusionFilterSpanIds) {
        const chunk = DgmlInstancesSingleton.getInstanceById(folderId, filterSpanId);
        if (!!chunk) {
            exclusionFilterItems.push({
                tokenRange: chunk.tokenRange!,
                spanId: chunk.spanId!
            })
        }
    }

    const filtered: TokenAndSpan[] = [];
    for (const spanId of spanIds) {
        const chunk = DgmlInstancesSingleton.getInstanceById(folderId, spanId);
        if (!!chunk) {
            if (chunk.tokenRange?.fromTok === chunk.tokenRange?.toTok) {
                continue;
            }

            const current: TokenAndSpan = {
                spanId: spanId,
                tokenRange: chunk.tokenRange!
            };

            let replaceIndex = -1;
            let skip = false;

            // Any conflict with an exclustion filter immediately disqualifies a chunk
            for (const filterItem of exclusionFilterItems) {
                if (filterItem.spanId === current.spanId || TokenRangeUtils.rangeIsContained(filterItem.tokenRange, current.tokenRange) || TokenRangeUtils.rangeIsContained(current.tokenRange, filterItem.tokenRange)) {
                    // This chunk conflicts with one of the filter chunks
                    skip = true;
                    break;
                }
            }

            if (!skip) {
                // Check existing items to see if we can add this chunk
                for (let i = 0; i < filtered.length; ++i) {
                    const existingItem = filtered[i];
                    if (existingItem.spanId === current.spanId || isParent(current, existingItem)) {
                        // This chunk is the same or a parent of an existing one - drop it
                        skip = true;
                        break;
                    }
                    else if (isParent(existingItem, current)) {
                        // Existing chunk is a parent of the current one - replace it with the child
                        replaceIndex = i;
                        break;
                    }
                }

                if (!skip) {
                    if (replaceIndex === -1) {
                        filtered.push(current);
                    }
                    else {
                        filtered.splice(replaceIndex, 1, current);
                    }
                }
            }
        }
    }

    return filtered.map(x => x.spanId);
}
